import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import authHeader from '../helpers/auth-header';
import { DateTime } from "luxon";
import { environment } from 'src/environments/environment';
import { IInvoice } from '../interfaces/iinvoice';
import { catchError, map } from 'rxjs';
import { IPayment } from '../interfaces/i-payment';

@Injectable({
     providedIn: 'root'
})
export class InvoiceService {

     constructor(private http: HttpClient) { }

     getInvoices(): any {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          // above returns format of 2021-08-01T01:00:00-05:00
          let endDate = DateTime.now().endOf('month').toISO();
          // Remove TZ portion for desired yyyy-MM-dd'T'HH:mm:ss
          endDate = endDate!.substr(0, endDate!.length - '.999-05:00'.length);
          let startDate = DateTime.now().minus({ months: 4 }).endOf('month').toISO();
          startDate = startDate!.substr(0, startDate!.length - '.999-05:00'.length);

          const url = `${environment.APP_API_URL}/api/v1/Invoice/Invoices?startDate=${startDate}&endDate=${endDate}`;
          return this.http.get(url, config)
               .pipe(
                    map((response: any) => {
                         if (response) {
                              let returnData: IInvoice[] = response.filter((i: IInvoice) => i.invoiceType === "Invoice");
                              returnData = returnData.map((i: IInvoice) => {
                                   i.paidInFull = (i.outstandingAmount === "0.00" ? "Paid" : "Unpaid")
                                   return i;
                              });
                              return returnData;
                         }
                         return null;

                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );
     }

     getInvoicesByBillCycle(billCycle: string): any {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          //parse billCycle into month and year
          const month = parseInt(billCycle.substring(0, 2));
          const year = parseInt(billCycle.substring(2, 46));

          let startDate = DateTime.fromObject({ year: year, month: month, day: 1 }) // Luxon month is 1-indexed
               .minus({ months: 1 })
               .endOf('month')
               .toISODate();

          let endDate = DateTime.fromObject({ year: year, month: month, day: 1 }) // Luxon month is 1-indexed
               .minus({ months: 0 })
               .endOf('month')
               .toISODate();



          const url = `${environment.APP_API_URL}/api/v1/Invoice/Invoices?startDate=${startDate}T23:59:59&endDate=${endDate}T23:59:59`;
          return this.http.get(url, config)
               .pipe(
                    map((response: any) => {
                         if (response) {
                              let returnData: IInvoice[] = response.filter((i: IInvoice) => i.invoiceType === "Invoice");
                              returnData = returnData.map((i: IInvoice) => {
                                   i.paidInFull = (i.outstandingAmount === "0.00" ? "Paid" : "Unpaid")
                                   return i;
                              });
                              return returnData;
                         }
                         return null;

                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );

     }

     pay(paymentRequest: IPayment, invoiceList: number, isPaymentCard: boolean, accountNumber: string, remaining: string) {


          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          const noteDate = DateTime.now().toISO();
          const url = `${environment.APP_API_URL}/api/v1/BillingAccount/PayInvoices?note=${noteDate} ${invoiceList}&isCard=${isPaymentCard}&accNumber=${accountNumber}&remaining=${remaining}`;
          return this.http.post(url, paymentRequest, config)
               .pipe(
                    map((response: any) => {
                         if (response) {
                              return response;
                         }
                         return null;

                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );


     }

     getInvoiceDetails(invoiceId: number, customerName: string) {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          const url = `${environment.APP_API_URL}/api/v1/Invoice/GetInvoiceDetailsPDF/${invoiceId}/${customerName}`;
          return this.http.get(url, config)
               .pipe(
                    map((response: any) => {
                         if (response) {
                              return response;
                         }
                         return null;

                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );
     }

     getBillingSummaryReport(custId: number, billCycle: string, reportType: string, login: string, showTotalsOnly: string, orderByLogins: string, withTransactionMemo: string) {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };

          // web api won't take a null parameter, so if login is empty, set it to 'null', api will handle it
          login = (login === '') ? 'null' : login;

          const url = `${environment.APP_API_URL}/api/v1/Invoice/GetBillingSummaryReport/${custId}/${billCycle}/${reportType}/${login}/${showTotalsOnly}/${orderByLogins}/${withTransactionMemo}`;

          return this.http.get(url, config)
               .pipe(
                    map((response: any) => {
                         if (response) {
                              return response;
                         }
                         return null;

                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );
     }

     GetGenericStatementReport(custId: number, billCycle: string) {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          const url = `${environment.APP_API_URL}/api/v1/Invoice/GetGenericStatementReport/${custId}/${billCycle}`;
          return this.http.get(url, config)
               .pipe(
                    map((response: any) => {
                         if (response) {
                              return response;
                         }
                         return null;

                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );
     }

     GetInvoicePdfById(custId: number, invoiceId: number) {
          const config = {
               headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    'Authorization': authHeader()
               }
          };
          const url = `${environment.APP_API_URL}/api/v1/Invoice/GetInvoicePdfById/${custId}/${invoiceId}`;
          return this.http.get(url, config)
               .pipe(
                    map((response: any) => {
                         if (response) {
                              return response;
                         }
                         return null;

                    }),
                    catchError(error => {
                         console.error(error);
                         return [];
                    })
               );
     }




     getCdbBillingCyclesList() {
          const startYear = "2010";
          const startMonth = "04"
          const currentYear = new Date().getFullYear();
          const currentMonth = new Date().getMonth() + 1;
          const currentMonthString = currentMonth < 10 ? '0' + currentMonth : currentMonth.toString();
          const currentPeriod = currentMonthString + currentYear.toString();
          const currentPeriodNumber = parseInt(currentPeriod);
          const periodList = [];
          // begin with startMonth and startYear and iterate the months until currentMonth and currentYear
          for (let i = parseInt(startYear); i <= currentYear; i++) {
               const year = i.toString();
               const startMonthNumber = i === parseInt(startYear) ? parseInt(startMonth) : 1;
               const endMonthNumber = i === currentYear ? currentMonth : 12;
               for (let j = startMonthNumber; j <= endMonthNumber; j++) {
                    const month = j < 10 ? '0' + j : j.toString();
                    const period = month + year;
                    const testPeriod = (new Date(parseInt(year), parseInt(month), 1))
                    const testCurrentPeriod = (new Date(currentYear, currentMonth, 1))
                    if (testPeriod <= testCurrentPeriod) {
                         periodList.push(period);
                    }
               }
          }
          // reverse the list
          return periodList.reverse();
     }
}
